const diseaseContent = {
  'atherosclerosis': {
'about': "Atherosclerosis is a chronic inflammatory response to endothelial injury, leading to increased permeability, leukocyte adhesion, and thrombosis.²",
'pathogenesis': `Atherosclerosis is a chronic inflammatory response to endothelial injury, leading to increased permeability, leukocyte adhesion, and thrombosis. This triggers lipoprotein accumulation, macrophage activation, platelet adhesion, and smooth muscle cell (SMC) recruitment. Lipids accumulate in macrophages and SMCs, promoting their proliferation, extracellular matrix (ECM) production, and T-cell recruitment.²
    

Fatty plaques (atheromas) form in arteries, narrowing the lumen (stenosis), restricting blood flow (ischemia), and potentially causing organ damage or necrosis. Early lesions, called fatty streaks, contain lipid-filled macrophages and may develop into raised atherosclerotic plaques, which are patchy and vary in size. Plaques form preferentially in areas of disturbed blood flow, like vessel branch points.²

Three Components of Atherosclerotic Plaques:
- Cells (SMCs, macrophages, T cells)
- ECM (collagen, elastic fibers, proteoglycans)
- Lipids (cholesterol, triglycerides)
The fibrous cap consists of SMCs and dense collagen, while the necrotic core contains cholesterol crystals, debris, foam cells, fibrin, and plasma proteins. Small blood vessels proliferate at the plaque's periphery, and the underlying media may show fibrosis and atrophy. Over time, plaques grow through cell death, ECM remodeling, thrombus organization, and dystrophic calcification.²`,
    
'symptoms': `Common symptoms include²:
- Chest pain or angina
- Shortness of breath
- Heart attack
- Stroke
- Peripheral artery disease
- Weakness in legs
- Pain in affected areas`,
    
'prognosis': `The prognosis of atherosclerosis depends on the vessel size, the plaque size and stability, and the degree of vessel disruption by the plaques. Major consequences of atherosclerosis include heart attack, stroke, aortic aneurysms, and peripheral vascular disease (gangrene of the lower extremities) - all related to ischemia of the heart, brain, kidneys, and lower extremities².

    Atherosclerotic plaques are prone to rupture, potentially causing blood clots (thrombosis) and sudden blockage of the vessel. Thickened areas in the vessel lining may also reduce blood flow to the underlying wall, leading to oxygen deprivation. This, along with tissue changes caused by inflammation, can weaken the vessel wall and increase the risk of blood vessel bulges (aneurysms) forming².

    Vulnerable plaques—those with a high risk of rupture—include plaques with abundant foam cells and extracellular lipids, plaques with thin fibrous caps containing few smooth muscle cells, and plaques with inflammatory cell clusters. Although stable plaques are less likely to rupture, they can undergo surface erosion and thrombosis, leading to rapid plaque growth, increased calcifications, and sudden cardiac death.²`,
    
'risk-factors': `The risk factors for atherosclerosis are approximately multiplicative, so two factors increase the risk 4-fold and three increase the risk 7-fold.²

Modifiable Risk Factors:
- Hyperlipidemia
- Hypertension
- Cigarette smoking
- Diabetes mellitus
- Inflammation
Low-density lipoprotein (LDL) cholesterol is the main component responsible for the increased risk of atherosclerosis associated with hyperlipidemia. LDL distributes cholesterol to peripheral tissues whereas high-density lipoprotein (HDL) cholesterol transports cholesterol from vascular plaques to the liver to be excreted. Reduced LDL levels and higher HDL levels are associated with reduced atherosclerosis risk. Hyperlipidemia is sufficient to induce atherosclerosis without other risk factors.²
Diabetes is associated with increased blood cholesterol levels. Individuals with diabetes are twice as likely to have a heart attack and 100 times more likely to experience atherosclerosis-induced gangrene of the lower extremities. Finally, inflammatory cells are associated with vascular plaque progression. Levels of C-reactive protein (CRP), a marker of inflammation, can independently predict the risk for consequences of atherosclerosis.²

Non-Modifiable Risk Factors:
- Genetics
- Increasing age
- Sex
- Obesity
- Type A personality
- Stress
- Elevated serum homocysteine and C-reactive protein levels
The most important independent risk factor for atherosclerosis is family history as certain mendelian disorders are strongly associated with atherosclerosis like familial hypercholesterolemia. Most familial risk is related to multifactorial traits like diabetes and hypertension. Atherosclerosis is generally asymptomatic until middle age, and so increased age is a risk factor for atherosclerosis and its complications. Males are more susceptible to atherosclerosis than their female counterparts. Atherosclerosis complications are uncommon in premenopausal women without other predisposing factors. The incidence increases and can exceed that in men post-menopause, typically explained by the decrease in estrogen.²`,
    
'diagnosis-and-treatment': `DIAGNOSIS:
The following list includes the main blood tests that are used to diagnose atherosclerosis.²

Blood Tests and Imaging:
- C-reactive protein (CRP) test: Checks for a protein related to artery inflammation.³
- Electrocardiogram (ECG or EKG): Measures the heart's electrical activity and can show if there's reduced blood flow to the heart. Sensors are attached to the chest, arms or legs and connect to a machine, which displays or prints the results.³
- Exercise stress tests: Heart activities are monitored while the patient rides a stationary bike or walks on a treadmill. May expose heart conditions that may otherwise be missed because of the increased heart rate during exercise. Medicine that stimulates the heart like exercise does is given to patients unable to exercise.³
- Echocardiogram: Sound waves reveal blood flow through the heart and the size and shape of heart structures. May be done during an exercise stress test.³
- Doppler ultrasound: A special ultrasound device checks the speed of blood flow in the arteries at different points in the patient's body which may expose any narrowed areas.³
- Ankle-brachial index (ABI): Compares the blood pressure in the ankle with the blood pressure in the arm to check for atherosclerosis in the arteries of the legs and feet.³
- Cardiac catheterization and angiogram: Reveals if the coronary arteries are narrowed or blocked. A long, thin flexible tube is inserted in a blood vessel and guided into the heart. Dye flows through the tube into the heart and defines the arteries more clearly in any images taken.³
- Coronary calcium scan or heart scan: Computerized tomography (CT) imaging is used to reveal calcium deposits in the walls of arteries. The test results are given as a score, with a higher calcium score relating to a higher heart attack risk. This test may show coronary artery disease before symptoms appear.³

TREATMENT:
Atherosclerosis may be treated through lifestyle changes, heart surgery, and/or medicine. In some cases, lifestyle changes are sufficient to treat atherosclerosis.²

Lifestyle Changes:
- Healthy diet²
- More exercise²
- Avoiding smoking²
- A healthy weight²
- Managing stress²
- Controlling blood pressure²
- Limiting alcohol²
- Good sleep²

Obesity and smoking lower HDL levels, increasing atherosclerosis risk. Exercise raises HDL levels, decreasing atherosclerosis risk. It is advised to avoid smoking, vaping or tobacco use, as smoking is a major risk factor for coronary artery disease caused by atherosclerosis. Smoking damages the arteries and nicotine tightens blood vessels. Reducing stress is also recommended to manage atherosclerosis. This can be achieved through exercise, mindfulness, yoga or deep breathing which temporarily lower blood pressure, reducing the risk of atherosclerosis.²

Medicinal Treatments:
- Statins²
- Aspirin²
- Blood pressure medicine²
- Fibrinolytic therapy²

Statins lower LDL levels by inhibiting hydroxymethylglutaryl coenzyme A (HMG-CoA) reductase which is the rate-limiting enzyme in cholesterol biosynthesis. Aspirin thins the blood, preventing blood clots. In fibrinolytic therapy a clot-dissolving medicine is used to degrade clots in the arteries, typically in an emergency situation.²

Surgical Treatments:
- Angioplasty²
- Stent placement²
- Endarterectomy²
- Coronary artery bypass grafts (CABG)²

Angioplasties and stent placements open blocked arteries. A catheter is guided to the clogged part of the artery and a tiny balloon is inflated to widen it and improve blood flow. A stent, a small mesh tube, may be used to hold the artery open and may slowly release medicine to further help keep the arteries open. Endarterectomies remove fatty plaques from the walls of the blocked artery. In CABG surgeries, a healthy blood vessel from somewhere else in the body is used to create a new path for blood flow in the heart.²`,
    
'learning-tools': `AI-generated Case Study:
A 53-year-old male arrived at the emergency department with severe chest pain that began an hour prior to arrival. He described the pain as a heavy pressure that radiated to his left arm. He also reported shortness of breath, excessive sweating, nausea, and dizziness. His medical history included high blood pressure (diagnosed 10 years ago), Type 2 diabetes (5 years), and high cholesterol (4 years). His lifestyle factors included a sedentary routine, a 25-year history of smoking, and a diet high in unhealthy fats.
On examination, his blood pressure was elevated at 160/95 mmHg, his heart rate was 105 beats per minute, and his respiratory rate was 22 breaths per minute, indicating faster-than-normal breathing. Further examination revealed cool feet and weak pulses in his legs, suggesting poor blood flow to the lower extremities.

AI-generated Flashcards:
Click here to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/2WA9XoqaY/">Access Atherosclerosis Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of atherosclerosis, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with atherosclerosis, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of atherosclerosis.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for atherosclerosis, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of atherosclerosis through interactive case studies and self-assessment tools.'
  },




  'tay-sachs': {
'about': "Tay-Sachs disease is a rare, inherited neurodegenerative disorder caused by an inability to metabolize GM2 gangliosides due to a lack of the β subunit of lysosomal hexosaminidase.²",
'pathogenesis': `Disease Mechanism²:
Tay-Sachs disease, the most common of all gangliosidosis, is caused by loss-of-function mutations of the β subunit of the enzyme hexosaminidase A, which is necessary for the degradation of GM2. More than 100 mutations have been described, most disrupting protein folding or intracellular transport. This results in the accumulation of GM2 gangliosides, particularly in neurons, axon cylinders of nerves, and glial cells throughout the CNS.²

Cellular Changes²:
Affected cells appear swollen or 'foamy' with lipid vacuolation leading to cellular dysfunction, neurodegeneration, and ultimately cell death. The disease particularly affects neurons throughout the central nervous system, causing progressive damage and deterioration of nerve function.²`,
    
'symptoms': `Early manifestations of Tay-Sachs disease typically begin around 3-6 months of age with progressive neurodegeneration, leading to severe mental retardation, blindness, motor weakness, and eventual death by 2 to 3 years of age. Common manifestations include loss of motor skills, exaggerated startle response, seizures, blindness and deafness, muscle weakness and paralysis. A characteristic cherry-red macula is often present on retinal examination. Initially, infants show normal development for the first few months before experiencing a gradual loss of abilities and decreased eye contact.²

Infantile Form (Classic)⁸:
- Onset between 3–5 months of age
- Feeding difficulties
- Lethargy (general weakness)
- Exaggerated startle reflex to sudden loud noises
- Progressive motor delays
- Mental deterioration
- Cherry-red spots in the eyes (90% of cases)
- Rapid disease progression, leading to severe neurological decline

Late-Onset Form⁸:
- Symptoms appear from adolescence to mid-30s
- Highly variable symptoms from case to case
- Slower progression compared to the infantile form`,

'prognosis': `Disease Forms and Progression:

Infantile Form²:
-Symptom onset at 3-6 months
-Rapid disease progression
-Limited survival (4-5 years)
-Poor developmental outcomes
-Fatal by 3–5 years

Later-onset Forms²:
-Juvenile/adult variants
-Later symptom presentation
-Variable disease progression
-Onset in adolescence or adulthood
-More diverse clinical course
-Milder but still progressive
-Causing neurological decline
-Reduced lifespan`,
    
'risk-factors': `Population Risk²:
-Ashkenazi Jewish ancestry (1 in 30 carrier rate)
-Cajun population
-French-Canadian ancestry
-Parental carrier status
-Family history of disease
-Carrier parents (autosomal recessive inheritance)`,
    
'diagnosis-and-treatment': `DIAGNOSIS²:
Diagnosis involves a combination of clinical evaluation, biochemical testing, and genetic analysis. For Types A and B, enzyme assay measuring sphingomyelinase activity in leukocytes is crucial. Type C diagnosis relies on the filipin staining test to identify cholesterol accumulation, along with genetic testing for NPC1 or NPC2 mutations. Additional testing includes blood chemistry analysis and imaging studies to assess organomegaly.
Testing Methods:
-Enzyme assay for hexosaminidase A activity
-Genetic testing for HEXA mutations
-Retinal exam showing cherry-red spot
-Enzyme activity testing in blood
-Hex-A level measurement
-Family history assessment
Prenatal Screening:
-Chorionic villus sampling (CVS)
-Amniocentesis testing
-Carrier screening
-Genetic counseling

TREATMENT²:
Currently, there is no definitive cure for Niemann-Pick disease. Treatment focuses on supportive care and symptom management. Type C patients may benefit from miglustat therapy, which can help slow disease progression. Management includes physical therapy, occupational therapy, speech therapy, respiratory support, and nutritional management. Regular monitoring of neurological function, organ status, growth, and development is essential.
Current Management:
-No cure; supportive care only
-Physical therapy programs
-Occupational therapy
-Respiratory support
-Nutritional management
-Regular monitoring
-Research participation options

Research Directions:
-Gene therapy under investigation
-Enzyme replacement therapy research
-Experimental treatments in development`,
    
'learning-tools': `AI Generated Case Study:
A 7-month-old infant is brought to the pediatrician by concerned parents who report that their child is not reaching developmental milestones. The baby initially displayed normal motor development but has since exhibited progressive weakness, decreased muscle tone, and an exaggerated startle response to loud noises. Parents also note that the infant appears less responsive to visual stimuli.
On physical examination, the infant has hypotonia, poor head control, and hyperreflexia. Fundoscopic examination reveals a characteristic cherry-red spot on the macula. Given the infant's Ashkenazi Jewish ancestry, the clinician suspects a lysosomal storage disorder.


AI-generated Flashcards:
Use the following link to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/2WA9XoqaY/">Access Tay-Sachs Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of Tay-Sachs disease, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with Tay-Sachs disease, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of Tay-Sachs disease.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for Tay-Sachs disease, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of Tay-Sachs disease through interactive case studies and self-assessment tools.'
  },



  
  'gaucher': {
    'about': "Gaucher disease is the most common lysosomal storage disorder caused by a deficiency in the enzyme glucocerebrosidase, leading to the accumulation of glucocerebroside in macrophages, resulting in organ and tissue damage²",
    
    'pathogenesis': `Disease Mechanism²:
Gaucher disease results from lack of the lysosomal enzyme glucocerebrosidase and accumulation of glucocerebroside in mononuclear phagocytic cells. In the most common type 1 variant, affected phagocytes become enlarged (Gaucher cells) and accumulate in liver, spleen, and bone marrow, causing hepatosplenomegaly and bone erosion. Types 2 and 3 are characterized by variable neuronal involvement.²

Cellular Effects²:
Glucocerebrosides are continually formed from the catabolism of glycolipids derived mainly from cell membranes of senescent leukocytes and red cells. Pathologic changes are caused by both the burden of storage material and the activation of macrophages, leading to the secretion of cytokines such as interleukin (IL)-1, IL-6, and tumor necrosis factor (TNF).²`,
    
    'symptoms': `Type 1 (Non-neuronopathic)²:
- Hepatosplenomegaly (enlarged liver and spleen)
- Massive spleen enlargement
- Skeletal involvement:
  - Osteopenia
  - Lytic lesions
  - Osteonecrosis
- Hematologic abnormalities:
  - Anemia
  - Thrombocytopenia
  - Easy bruising

Type 2 (Acute neuronopathic)²:
- Severe neurologic symptoms
- Convulsions
- Mental deterioration
- Onset in infancy
- Fatal by 2-3 years of age

Type 3 (Chronic neuronopathic)²:
- Later onset than Type 2
- Slower disease progression
- Variable neurologic involvement
- Combination of Type 1 systemic symptoms and neurological manifestations`,
    
    'prognosis': `Disease Types and Outcomes²:

    Type 1 (Non-neuronopathic):
- Most common form
- Normal life expectancy with treatment
- Common in European Jewish descent
- Responds well to early intervention
- Manageable long-term prognosis

Type 2 (Acute Neuronopathic):
- Severe neurological involvement
- Early onset of symptoms
- Fatal in infancy
- Poor overall prognosis

Type 3 (Chronic Neuronopathic):
- Variable disease progression
- Survival possible into adulthood
- Treatment response variable
- Shortened lifespan, varies with severity`,
    
    'risk-factors': `Genetic and Population Risk²:
- Ashkenazi Jewish ancestry (especially Type 1)
- Family history of Gaucher disease
- Autosomal recessive inheritance
- Parental carrier status
- Ethnic background
- Consanguinity`,
    
    'diagnosis-and-treatment': `DIAGNOSIS:
Diagnosis involves enzyme assay measuring glucocerebrosidase activity in leukocytes or fibroblasts, genetic testing to identify GBA mutations, and bone marrow biopsy showing characteristic Gaucher cells with a "wrinkled tissue paper" appearance. Regular monitoring includes assessment of organ involved, bone density, and hematologic parameters².

    Enzyme Analysis²:
- Glucocerebrosidase activity testing
- White blood cell analysis
- Quantitative enzyme assessment
- Blood tests for biomarkers
Genetic Testing²:
- GBA gene mutation analysis
- Variant identification
- Family screening
- Carrier detection
Tissue Studies²:
- Bone marrow biopsy showing Gaucher cells
- Organ involved assessment
- Disease staging
Imaging Studies²:
- MRI of bones and organs
- X-rays for bone involvement
- CT scans when indicated
- Bone density measurements

TREATMENT:
Treatment options have expanded significantly, with enzyme replacement therapy (ERT) using intravenous recombinant glucocerebrosidase being the primary intervention for managing systemic symptoms. Substrate reduction therapy (SRT) with oral inhibitors of glucosylceramide synthase provides an alternative approach. Emerging treatments include gene therapy through hematopoietic stem cell transplantation. Supportive care includes management of bone pain, anemia, and other specific symptoms.²
Enzyme Replacement Therapy (ERT)²:
- Imiglucerase and other recombinant enzymes
- Intravenous administration
- Regular infusion schedule
- Systemic symptom management
- Dose adjustment based on response
Substrate Reduction Therapy (SRT)²:
- Oral medications (miglustat, eliglustat)
- Production limitation approach
- Regular efficacy monitoring
Supportive Care²:
- Bone pain management
- Anemia management and blood transfusions
- Iron supplementation when needed
- Splenomegaly monitoring
- Regular assessment
- Multidisciplinary approach
- Physical therapy
- Occupational therapy
- Nutritional support`,
    
'learning-tools': `AI Generated Case Study:
A 12-year-old boy presents with complaints of chronic fatigue, easy bruising, and persistent abdominal fullness. His parents have noticed that he frequently experiences bone pain, particularly in the legs, and that he has had multiple unexplained nosebleeds. On examination, the boy appears pale, with significant hepatosplenomegaly. His weight is in the 10th percentile for age, and his growth velocity has declined over the past two years.
Blood tests reveal anemia, thrombocytopenia, and elevated ferritin levels. A bone marrow biopsy shows the presence of Gaucher cells, which are lipid-laden macrophages with a characteristic "wrinkled tissue paper" appearance. Enzyme assay demonstrates a marked reduction in glucocerebrosidase activity, and genetic testing confirms the presence of biallelic pathogenic mutations in the GBA gene.

AI-generated Flashcards:
Use the following link to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/2WA9XoqaY/">Access Gaucher Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of Gaucher disease, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with Gaucher disease, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of Gaucher disease.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for Gaucher disease, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of Gaucher disease through interactive case studies and self-assessment tools.'
  },





  'niemann-pick': {
    'about': "A group of inherited lysosomal storage diseases caused by the defective metabolism of sphingomyelin due to a lack of the enzyme sphingomyelinase. This leads to accumulation in various tissues. The disease is divided into Types A, B, and C based on clinical features and genetics.²",
    
    'pathogenesis': `Disease Mechanisms²:
Type A/B Mechanism²:
Niemann-Pick disease types A and B are caused by a deficiency of sphingomyelinase (ASM) due to SMPD1 mutations. In the more severe type A variant, accumulation of sphingomyelin in the nervous system results in neuronal damage. Sphingomyelin also accumulates in phagocytes within the liver, spleen, bone marrow, and lymph nodes, causing their enlargement. In type B, neuronal damage is not present, as mutant ASM retains some activity.²

Type C Mechanism²:
Niemann-Pick disease type C is caused by a defect in cholesterol transport (NPC1 or NPC2 mutations) and resultant accumulation of cholesterol and gangliosides in the nervous system. This leads to impaired lysosomal-to-cytoplasmic cholesterol transport, causing cells to accumulate both cholesterol and gangliosides (GM1, GM2).²`,
    
    'symptoms': `Type A:
- Severe neurologic deterioration from early infancy
- Massive organomegaly
- Progressive loss of motor skills
- Feeding difficulties
- Progressive learning disabilities
- Seizures
- Cherry-red macula
- Rapid disease progression²,⁸

Type B:
- Organomegaly without neurological symptoms
- Hepatosplenomegaly
- Respiratory issues
- Growth delays
- Blood abnormalities
- No neurological involvement²

Type C:
- Progressive neurological impairment
- Ataxia
- Vertical supranuclear gaze palsy
- Dystonia
- Dysarthria
- Psychomotor regression
- Progressive loss of motor skills
- Feeding difficulties
- Progressive learning disabilities
- Seizures²,⁸

Common Features Across Types²:
- Hepatosplenomegaly (except some Type C cases)
- Progressive neurodegeneration (except Type B)
- Hypotonia
- Dysphagia
- Development regression (Types A and C)`,
    
    'prognosis': `Disease Course by Type:

    Type A:
- Rapid neurodegeneration
- Fatal by age 3
- Poor developmental outcomes
- Limited treatment options²

Type B:
- Variable disease severity
- Survival into adulthood possible
- Better overall prognosis
- Response to interventions varies²

Type C:
- Progressive neurodegeneration
- Fatal in childhood or early adulthood
- Variable rate of progression
- Dependent on age of onset²`,
    
    'risk-factors': `Genetic and Population Risk²:
- Ashkenazi Jewish ancestry (especially Type A)
- Autosomal recessive inheritance
- Family history of Niemann-Pick disease
- Parental carrier status
- Consanguinity`,
    
    'diagnosis-and-treatment': `DIAGNOSIS²:
Diagnosis involves a combination of clinical evaluation, biochemical testing, and genetic analysis. For Types A and B, enzyme assay measuring sphingomyelinase activity in leukocytes is crucial. Type C diagnosis relies on the filipin staining test to identify cholesterol accumulation, along with genetic testing for NPC1 or NPC2 mutations. Additional testing includes blood chemistry analysis and imaging studies to assess organomegaly.
    Laboratory Testing:
- Enzyme assay for sphingomyelinase activity (Types A/B)
- Filipin staining test for cholesterol accumulation (Type C)
- Genetic testing for specific mutations:
  - SMPD1 (Types A & B)
  - NPC1 or NPC2 (Type C)
- Blood chemistry analysis
Imaging Studies²:
- Ultrasound examination
- CT scanning
- MRI of brain
- Assessment of organomegaly

TREATMENT:
Disease Management²:
- No cure currently available
- Supportive care for symptoms
- Miglustat therapy for Type C (slows progression)
- Physical therapy
- Occupational therapy
- Speech therapy
- Respiratory support
- Nutritional management
Monitoring Protocol²:
- Regular neurological assessment
- Organ function monitoring
- Growth tracking
- Development evaluation`,
    
'learning-tools': `AI Generated Case Study:
A 3-year-old girl is brought to a pediatric neurologist due to developmental regression, frequent falls, and difficulty swallowing. Her parents report that she was initially meeting developmental milestones but has lost previously acquired skills, including speech and motor coordination. She also suffers from recurrent respiratory infections and increasing feeding difficulties. On examination, she exhibits hypotonia, hepatosplenomegaly, and a cherry-red macula.
An enzyme assay demonstrates reduced sphingomyelinase activity, raising suspicion for Niemann-Pick disease types A or B. Genetic testing identifies biallelic pathogenic mutations in the SMPD1 gene, confirming the diagnosis. Brain MRI reveals cortical atrophy and white matter abnormalities, while lipid panel results show elevated cholesterol and sphingomyelin levels.

AI-generated Flashcards:
Click here to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/pN5iuD6ET/">Access Niemann-Pick Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of Niemann-Pick disease, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with Niemann-Pick disease, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of Niemann-Pick disease.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for Niemann-Pick disease, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of Niemann-Pick disease through interactive case studies and self-assessment tools.'
  },




  'pku': {
    'about': "PKU is an autosomal recessive metabolic disorder affecting amino acid metabolism, which results from a deficiency of phenylalanine hydroxylase (PAH), an enzyme necessary for converting phenylalanine into tyrosine.",
    'pathogenesis': `PKU is caused by mutations in the PAH gene (chromosome 12q23.2). Over 500 mutations have been identified, ranging from single nucleotide substitutions to deletions. These mutations can lead to partial or complete absence of the PAH enzyme activity, leading to an increase of Phenylalanine and partial to zero conversion of Phenylalanine to Tyrosine, which is required for melanin production. This autosomal recessive genetic disease affects 1/12,000 live births, predominantly in Northern European ancestry. Affected individuals inherit one mutated PAH gene from each parent and carriers (heterozygotes) are asymptomatic.⁸,¹¹ 
    The key effects that take place include⁸:
- Elevated blood phenylalanine levels, which lead to impairment in brain development in young children
- Tyrosine deficiency disrupts the production of melanin and neurotransmitters like norepinephrine and dopamine 
Neurological manifestations include¹³:
- Hypomyelination: conduction that reduces the ability to form myelin in nerve fibers, which can lead to motor problems, developmental problems, and other neurological issues.
- Microcephaly: a rare birth defect that causes a baby's head to be smaller than their expected for their age 
- Gliosis: a condition in which the brain creates more or larger glial cells that can causing scarring, preventing the brain from fully recovering from an injury, and therefore leading to neurodegenerative disorders

    Genetic Basis:
- PAH gene mutations (chromosome 12q23.2)
- Over 500 different mutations identified
- Types include single nucleotide substitutions and deletions
- Autosomal recessive inheritance pattern
- Affects 1/12,000 live births, predominantly in Northern European ancestry
- Carriers (heterozygotes) are asymptomatic
- Both parents must contribute one mutated PAH gene

Biochemical Mechanism:
- Deficiency of phenylalanine hydroxylase (PAH) enzyme
- Impaired conversion of phenylalanine to tyrosine
- Elevated blood phenylalanine levels
- Disrupted production of melanin and neurotransmitters (norepinephrine, dopamine)
- Partial to complete absence of PAH enzyme activity

Neurological Effects:
- Brain development impairment in young children
- Hypomyelination (reduced myelin formation in nerve fibers)
- Microcephaly (smaller head size than expected)
- Gliosis (increased glial cell formation and scarring)
- Motor and developmental problems
- Potential neurodegenerative complications

Unlike some other metabolic disorders (learn more about <Link to="/tay-sachs/pathogenesis">Tay-Sachs disease</Link> and <Link to="/galactosemia/pathogenesis">Galactosemia</Link>), PKU has no lysosomal storage in neurons. This distinguishing characteristic sets it apart from classical lysosomal storage disorders while still causing significant neurological impacts through other mechanisms.`,
    'symptoms': `Symptoms if untreated⁸:
- Intellectual disability
- Seizures
- Behavioral problems
- Light skin and hair
- Musty odor
- Skin conditions
- Growth problems`,
    'prognosis': `Untreated PKU Consequences⁸:
- Severe intellectual disability
- Seizures
- Hyperactivity
- Reduced pigmentation in hair and skin
- Irreversible brain damage
- Long-term neurological complications

Maternal PKU Syndrome:
- Elevated maternal phenylalanine acts as teratogen
- Causes severe congenital anomalies in fetus
- Results in intellectual disabilities in offspring
- Requires strict dietary control before and during pregnancy

Early Treatment Impact:
- Better cognitive outcomes with early intervention
- Prevention of most neurological complications
- Normal development possible with dietary compliance
- Lifelong management necessary for optimal outcomes`,
    'risk-factors': `Genetic Inheritance | Autosomal Recessive Pattern⁸: PKU occurs when an individual inherits two mutated copies of the PAH gene, one from each parent. Parents who are carriers (with one mutated gene) typically do not show symptoms but have a 25% chance of having an affected child if both are carriers. 

Ethnic and Geographical Prevalence¹¹:  PKU prevalence varies globally, with higher rates observed in certain populations. For example, Turkey has the highest documented rate, with 1 in 2,600 births affected, while countries like Finland and Japan have much lower rates, with fewer than one case per 100,000 births. 

Maternal PKU Syndrome | Teratogenic Effects During Pregnancy⁸: Women with PKU who do not maintain strict dietary control during pregnancy can expose the fetus to high phenylalanine levels, leading to maternal PKU syndrome. This syndrome is associated with severe congenital anomalies, intellectual disabilities, microcephaly, congenital heart disease, and intrauterine growth retardation in the offspring. 

Late Diagnosis and Dietary Management⁸: Without early diagnosis and dietary management, individuals with PKU are at risk for severe intellectual disability, seizures, hyperactivity, and reduced pigmentation in hair and skin due to tyrosine deficiency. Early detection through newborn screening and adherence to a low-phenylalanine diet are crucial for preventing these adverse outcomes.`,
    'diagnosis-and-treatment': `DIAGNOSIS¹⁰:
Primary Screening:
- Newborn screening within first week of life
- Guthrie test-serum analysis: also known as the heel prick test, which uses a bacterial inhibition assay to test a small blood sample from a newborn's heel for the presence of phenylalanine
- Tandem mass spectrometry: involves two separate stages of mass analysis and can be used to decipher relationships among ions in a mass spectrum or to identify compounds in complex mixtures that have not been subjected to prior separation.
- Detects elevated phenylalanine levels in blood

Screening Methods:
Guthrie test-serum (heel prick test)
- Uses bacterial inhibition assay
- Tests blood sample from newborn's heel
- Specifically detects phenylalanine presence
Tandem Mass Spectrometry:
- Two-stage mass analysis process
- Identifies compounds in complex mixtures
- No prior separation required
- Helps decipher relationships among ions
Confirmatory Testing:
- Plasma amino acid analysis
- Confirms high phenylalanine levels
- Checks for low tyrosine levels
- Provides detailed metabolic profile
Genetic Analysis:
- Identifies specific PAH mutations
- Assists with diagnosis confirmation
- Enables family counseling
- Helps determine inheritance patterns

TREATMENT⁸,¹¹:
Maternal PKU Management:
- Strict preconception dietary control
- Pregnancy phenylalanine level monitoring
- Prevention of fetal complications
- Maintenance of safe phenylalanine levels
Dietary Management:
- Low-phenylalanine diet
- Tyrosine supplementation
- Avoidance of high-protein foods (meat, fish, eggs, dairy)
- Elimination of aspartame-containing products (diet sodas)
- Use of phenylalanine-free medical formulas
- Regular nutritional assessment
- Strict dietary compliance required
Pharmacological Therapy:
- Sapropterin dihydrochloride (Kuvan)
- Enhances residual PAH enzyme activity
- Pegvaliase (Palynziq) for adults
- Enzyme substitution therapy
Monitoring and Follow-up:
- Frequent blood tests
- Target phenylalanine levels: 120–360 µmol/L
- Growth and development tracking
- Regular neurological assessment
- Dietary restriction adjustments
- Regular medical follow-up
Support Services:
- Genetic counseling
- Family education
- Psychological support
- Nutritional guidance
- Long-term management planning`,
    'learning-tools': `AI-generated Case Study:
Emily was born healthy and discharged from the hospital with no immediate concerns. However, at her two-week check-up, her newborn screening test results returned abnormal, indicating high levels of phenylalanine in her blood. The patient's physical examination revealed no immediate signs of distress, with normal weight gain observed. However, newborn screening results indicated elevated phenylalanine levels (>6 mg/dL, normal <2 mg/dL), prompting further testing. A follow-up plasma amino acid test confirmed a severely elevated phenylalanine level of 15 mg/dL, along with low tyrosine levels, suggesting a deficiency in phenylalanine metabolism. These findings are consistent with Phenylketonuria (PKU) and necessitate early dietary intervention to prevent neurological complications.

AI-generated Flashcards:
Click here to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/2WA9XoqaY/">Access PKU Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of PKU, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with PKU, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of PKU.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for PKU, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of PKU through interactive case studies and self-assessment tools.'
  },



  
  'diabetes': {
    'about': `Diabetes mellitus (DM) is a complex metabolic disorder characterized by hyperglycemia. This condition arises due to: 1) defective insulin secretion, leading to absolute lack of insulin; 2) defective insulin action, leading to relative lack of insulin; 3) Combination of both.²`,
    'pathogenesis': `Diabetes is classified into several types, but the two most common forms are Type 1 diabetes (T1D) and Type 2 diabetes (T2D). While both conditions result in hyperglycemia, their underlying mechanisms differ.

    Type 1 Diabetes (T1D):
Type 1 diabetes (T1D), also referred to as type 1A diabetes, is an autoimmune disorder marked by the targeted destruction of pancreatic β-cells, which are responsible for producing insulin. This immune-mediated process leads to absolute insulin deficiency and the development of chronic hyperglycemia.
The pathogenesis begins with a loss of immune self-tolerance to β-cell antigens.:
- Normally, self-reactive T-cells are eliminated during thymic development or regulated by peripheral mechanisms.
- In T1D, these tolerance checkpoints fail due to defects in negative selection or in the function of regulatory T-cells, allowing self-reactive T-cells to persist. These T-cells recognize β-cell-specific antigens and initiate a type IV hypersensitivity response, leading to progressive β-cell destruction.
A small subset of patients present with type 1B diabetes, or idiopathic diabetes, which lacks evidence of autoimmunity (i.e., no detectable autoantibodies or HLA associations). This variant is thought to involve different, possibly genetic, mechanisms.
Key Characteristics:
- Autoimmune disorder where the immune system attacks and destroys pancreatic β cells, leading to an absolute insulin deficiency.
- Accounts for 5–10% of all diabetes cases.
- Most commonly diagnosed in individuals under 20 years old.
Disease Progression:
The onset of T1D may appear to develop quickly, but the symptoms of the disease typically do not become apparent until over 90% of these pancreatic β-cells have been destroyed. The rate at which this autoimmune process unfolds can differ widely depending on the individual. 
- In children and adolescents, the process is usually rapid, often leading to complete insulin deficiency within a short time frame. This can result in diabetic ketoacidosis (DKA) as the initial manifestation. 
- In contrast, adults may experience a slower, more gradual progression of β-cell loss. This form called Latent Autoimmune Diabetes in Adults (LADA) may initially resemble type 2 diabetes, as patients retain some insulin function for months or years before becoming insulin-dependent.

Type 2 diabetes (T2D):
Type 2 diabetes (T2D) accounts for the majority of diabetes cases globally. Unlike type 1 diabetes, it is not driven by autoimmune mechanisms but instead results from a complex interplay of genetic susceptibility and environmental influences. The defining features of T2D are insulin resistance and progressive β-cell dysfunction, which together lead to chronic hyperglycemia.
Insulin Resistance:
Insulin resistance refers to a reduced responsiveness of target tissues—particularly the liver, striated muscle, and adipose tissue—to circulating insulin.
- In the liver, this impaired sensitivity means insulin fails to effectively suppress gluconeogenesis.
- In striated muscle, glucose uptake after meals is decreased.
- Additionally, in adipose tissue, the suppression of lipolysis is impaired, increasing the release of free fatty acids (FFAs).²
β-cell dysfunction:
While insulin resistance is a critical early component, overt diabetes does not develop until β-cells fail to compensate by adequately increasing insulin secretion. Initially, pancreatic β-cells increase insulin production in response to rising blood glucose and insulin resistance, resulting in hyperinsulinemia that helps maintain normoglycemia. Over time, however, β-cells begin to fail under chronic stress, leading to relative insulin deficiency.
Multiple factors contribute to β-cell dysfunction:
- Genetic variations that affect insulin secretion
- Lipotoxicity, where elevated FFAs impair β-cell function
- Glucotoxicity, or the damaging effects of chronic hyperglycemia
- Reduced incretin effect, leading to decreased insulinotropic hormone secretion
- Amyloid accumulation in the islets, seen in most individuals with T2D, though its role remains unclear
Key Characteristics:
- Develops due to a combination of insulin resistance and an inadequate insulin response from pancreatic β cells, leading to relative insulin deficiency.
- Accounts for 90–95% of diabetes cases.
- Strongly linked to obesity and lifestyle factors.²`,
    'symptoms': `Symptoms of Type 1 Diabetes often appear abruptly, typically over the course of a few weeks or months.¹⁷ 
Symptoms of Type 2 Diabetes usually emerge gradually, and some individuals may not notice any symptoms at all.¹⁷

Early Symptoms²,¹⁷:
-Increased thirst (polydipsia)
-Frequent urination (polyuria)
-Excessive hunger (polyphagia)
-Unexplained weight changes
-Fatigue
-Blurred vision

Advanced Symptoms¹⁷:
-Slow wound healing
-Recurrent infections
-Numbness in extremities
-Skin changes
-Mood changes
-Cognitive impacts

Complications-Related Symptoms²:
- Vision problems from retinopathy
- Nerve pain, foot problems, and sexual dysfunction from neuropathy
- Kidney function changes from nephropathy
- Cardiovascular symptoms
- Nausea and fruity-odor in breath from diabetes-related ketoacidosis (DKA)`,
    'prognosis': `The prognosis of diabetes varies significantly depending on several factors¹⁷:
- Type of diabetes
- Age at diagnosis
- Duration of the disease
- Comorbid conditions
- Management of the condition over time
- Access to treatment and healthcare
- Diabetes complications 
While individuals with well-controlled diabetes can lead long, healthy lives, chronic hyperglycemia can significantly shorten lifespan and impair quality of life if left untreated.


ACUTE COMPLICATIONS²:
Acute complications can be life-threatening. One of the most serious is diabetic ketoacidosis (DKA), typically seen in T1D. DKA is triggered by insulin deficiency, often due to missed doses or physiological stress (e.g., infection), leading to extreme hyperglycemia, dehydration, ketone accumulation, and metabolic acidosis. Symptoms include fatigue, nausea, vomiting, abdominal pain, fruity breath odor, and labored breathing (Kussmaul respiration). If untreated, DKA can progress to coma and death, but it is reversible with prompt administration of insulin and correction of underlying triggers.

CHRONIC COMPLICATIONS¹⁵:
Chronic complications arise from prolonged hyperglycemia and are classified into two main categories.
Microvascular Complications:
These affect small blood vessels throughout the body.
- Diabetic retinopathy, potentially causing vision impairment or blindness
- Diabetic nephropathy, a major cause of kidney failure and ESRD
- Diabetic neuropathy, resulting in pain, numbness, and increased risk of foot ulcers and amputation
- Increased susceptibility to infections
Macrovascular complications
- Accelerated atherosclerosis, increasing the risk of heart attacks, strokes, and peripheral artery disease.


Timeline and Progression:
Most chronic complications typically manifest 15-20 years after disease onset, with approximately 60-80% of patients developing retinopathy within this timeframe. The progression of nephropathy varies based on diabetes type and control. These complications can significantly impact quality of life and mortality, emphasizing the importance of early intervention and consistent management.`,
    'risk-factors': `TYPE 1 DIABETES RISK FACTORS²:
Type 1 diabetes is primarily an autoimmune disease with strong genetic predispositions and possible environmental triggers. Although it cannot be prevented, understanding the key risk factors helps in identifying individuals at increased risk.
    Genetic Susceptibility:
Genetics plays a major role in T1D risk. Genome-wide association studies (GWAS) have identified over 20 loci linked to increased susceptibility. The strongest association is with class II MHC (HLA-DR) genes, particularly HLA-DR3 and HLA-DR4. These alleles are present in approximately 90–95% of individuals with T1D of European descent, compared to only about 40% of the general population. Moreover, heterozygosity for DR3 and DR4 (DR3/DR4) is especially high-risk, seen in 40–50% of patients but only 5% of unaffected individuals.
However, carrying these alleles does not guarantee disease development—most individuals with high-risk HLA genotypes do not develop T1D, indicating that these genes predispose rather than directly cause the disease.
Other important non-HLA genetic risk factors include: 
Insulin (INS) gene polymorphisms, which may reduce insulin expression in the thymus and impair deletion of insulin-specific self-reactive T-cells.
CTLA-4 and PTPN22, genes involved in downregulating T-cell responses. Variants that impair their function may lead to excessive T-cell activation and loss of immune tolerance.
Environmental Triggers²:
Environmental factors are believed to play a role in triggering disease onset, particularly in genetically susceptible individuals. 
- Viral infections, such as coxsackievirus, which may damage pancreatic β-cells or trigger autoimmunity
- Changes in the gut microbiome, which can influence immune system development and regulation
Interestingly, some infections may actually be protective against T1D, though the mechanisms are not yet understood.²
Other Considerations¹⁷:
- Family history: Having a first-degree relative with T1D significantly increases risk
- Geographic and ethnic factors: T1D is more common in populations of European descent and in regions farther from the equator

TYPE 2 DIABETES RISK FACTORS²:
Type 2 diabetes arises from an interplay between inherited genetic risk and lifestyle-related influences, with obesity and insulin resistance at the core of its pathogenesis. Identifying and modifying key risk factors—especially obesity, inactivity, and poor diet—are central to both prevention and early intervention efforts.
Genetic Susceptibility²:
T2D has a strong familial component, with monozygotic twin concordance rates as high as 80–90%, significantly higher than that observed in Type 1 diabetes. Genome-wide association studies (GWAS) have identified dozens of susceptibility genes, though most of their exact roles remain unclear. These "diabetogenic" genes likely influence pathways related to β-cell function, insulin action, or glucose metabolism but do not involve immune tolerance pathways, further emphasizing the distinct pathogenesis of T2D. A positive family history, especially in first-degree relatives, is a strong predictor of disease risk.²
Obesity and Body Fat Distribution:
Obesity is the most significant environmental risk factor for T2D. Excess adipose tissue—especially central (visceral) obesity—is strongly linked to the development of insulin resistance, even before hyperglycemia appears.²
- Compared to peripheral fat, visceral fat is more lipolytic, releasing higher levels of free fatty acids (FFAs) into circulation. Excess FFAs accumulate in liver and muscle tissue, disrupting insulin signaling pathways.²
- Additionally, adipose tissue also secretes signaling molecules known as adipokines. Some adipokines (e.g., leptin, adiponectin) enhance insulin sensitivity, while others promote inflammation and insulin resistance.²
Metabolic Syndrome²:
Individuals with metabolic syndrome are at particularly high risk for developing T2D. 
- Central obesity
- Hypertension
- Dyslipidemia (high triglycerides, low HDL)
- Impaired glucose tolerance²
Lifestyle and Behavioral Factors:
A sedentary lifestyle, poor dietary habits, and lack of physical activity are well-established contributors to insulin resistance and weight gain. These modifiable risk factors are a key target for prevention and early intervention in at-risk populations.
Demographic Factors:
- Advancing age
- Belonging to high-risk ethnic groups, including; Native Americans, African Americans, Hispanic/Latino populations, and Asian Americans
These groups tend to have higher baseline susceptibility due to both genetic and socio-environmental factors. However, despite strong genetic associations, the exact genes involved remain not fully understood.`,
   
    'diagnosis-and-treatment': `DIAGNOSIS:
Diabetes is diagnosed based on chronic hyperglycemia, assessed through blood glucose testing.
According to the American Diabetes Association (ADA) and the World Health Organization (WHO), a diagnosis of diabetes is made when any one of the following criteria is met on two separate occasions, unless classic symptoms are present;
- Fasting blood glucose ≥126 mg/dL (no food or drink except water for 8+ hours)
- Random blood glucose ≥200 mg/dL with classic symptoms (e.g., polyuria, polydipsia, weight loss)
- 2-hour glucose ≥200 mg/dL during an oral glucose tolerance test (OGTT) with 75g glucose
- Hemoglobin A1c (HbA1c) ≥6.5% (reflects average blood glucose over 2–3 months)²
Note: Stress-related conditions (e.g., infection, trauma) can temporarily raise blood sugar levels. For a reliable diagnosis, hyperglycemia must persist after recovery from such events.²
Prediabetes Indicators²:
Prediabetes is a state of intermediate hyperglycemia that indicates increased risk for developing Type 2 diabetes and cardiovascular disease. 
Diagnostic thresholds include:
- Fasting glucose: 100–125 mg/dL
- 2-hour OGTT: 140–199 mg/dL
- HbA1c: 5.7–6.4%
Autoantibody Testing¹⁹:
In Type 1 diabetes, several autoantibodies targeting pancreatic β-cell antigens (e.g., insulin, glutamic acid decarboxylase) are often detected. These autoantibodies serve as important diagnostic markers of the autoimmune process. They are typically present before symptoms appear—sometimes years in advance—especially in those with a family history of autoimmune diseases. Their presence helps distinguish Type 1 diabetes from Type 2 and guide early screening in high-risk individuals.

TREATMENT:
Managing diabetes requires a comprehensive, individualized approach, focusing on the type of diabetes, glycemic control, prevention of complications, and patient preferences.¹⁷
Blood Glucose Monitoring¹⁷:
Monitoring helps assess how well treatments are working and guides treatment decisions about food, activity, and medications.
- Fingerstick glucose meters for immediate readings throughout the day
- Continuous glucose monitors (CGMs) for real-time tracking (especially in Type 1 diabetes)
Oral Medications¹⁷:
Primarily for Type 2 diabetes and improves insulin sensitivity, enhances insulin secretion, and lowers liver glucose production. Metformin is the first-line medication due to its efficacy, safety, and favorable effects on weight.
Insulin Therapy¹⁷:
Essential for Type 1 diabetes and required in some patients with Type 2 diabetes, especially when oral agents are insufficient. Different types of insulin vary in onset and duration and are tailored to individual treatment needs. 
Insulin can be administered using:
- Syringes
- Insulin pens
- Insulin pumps
- Inhaled insulin (rapid-acting)
Exercise¹⁷:
Physical activity improves insulin sensitivity and helps with weight management. The goal should be at least 150 minutes/week of moderate-intensity activity (e.g., walking, cycling).
Nutrition and Meal Planning¹⁷:
A balanced diet is key to managing blood sugar and preventing complications. For insulin users, carbohydrate counting is crucial to determine dosing. Emphasis on high-fiber, low-glycemic index foods, lean proteins, and healthy fats.
Cardiovascular Risk Management¹⁷:
People with diabetes are at higher risk for heart disease, so it's vital to also manage;
- Weight
- Blood pressure
- Cholesterol (especially LDL and HDL levels)
- Healthy lifestyle habits`,
    'learning-tools': `AI-generated Case Study:

Case Study 1:
A 12-year-old girl was brought to her pediatrician by her mother due to persistent fatigue, increased thirst, and frequent urination over the past several weeks. Her mother also reported that she had been waking up at night to use the bathroom and had recently started wetting the bed again. The child had experienced unexplained weight loss despite having a good appetite, and had also been complaining of blurry vision and abdominal discomfort.
Her past medical history was unremarkable, but her family history included an uncle with Type 1 diabetes. She had no known allergies and was up to date on all vaccinations. Her lifestyle included regular participation in school activities and no recent travel or illness. 
On examination, she appeared thin and mildly dehydrated. Her weight had dropped from the 50th to the 25th percentile over the last three months. Her vital signs were stable; blood pressure was 100/65 mmHg, heart rate was 110 beats per minute, and respiratory rate was 20 breaths per minute. No abnormalities were found on abdominal or neurological exams. 
Laboratory tests revealed a random blood glucose level of 19.2 mmol/L and a hemoglobin A1c (HbA1c) of 10.5%. Urine analysis was positive for glucose and ketones. Additional testing showed low C-peptide levels and positive anti-GAD65 and IA-2 autoantibodies.

Case Study 2:
A 48-year-old male visited his primary care clinic for a routine check-up but mentioned feeling increasingly tired over the past few months. He also reported increased thirst, frequent urination, and occasional blurry vision. He had gained around 10 kg over the past year and noted difficulty losing weight despite attempting to diet. He denied chest pain, fever, or recent illness.
His medical history included high blood pressure (diagnosed 3 years ago) and a sedentary lifestyle. His family history was significant for Type 2 diabetes in both parents. He worked a desk job, did not exercise regularly, and admitted to frequent consumption of fast food and sugary beverages.
On examination, his body mass index (BMI) was 32.5 kg/m², placing him in the obese category. His blood pressure was 145/90 mmHg, and his waist circumference measured 105 cm. No physical signs of acute illness were present. Neurological exam was unremarkable, but early signs of decreased vibration sensation were noted in both feet.
Laboratory testing showed a fasting blood glucose level of 9.2 mmol/L and an HbA1c of 8.1%. A lipid panel revealed elevated triglycerides and low HDL cholesterol. Urinalysis was positive for microalbumin, indicating early signs of kidney involvement.

AI-generated Flashcards:
Click here to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/ZWusYzrNb/">Access Diabetes Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of diabetes, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with diabetes, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of diabetes.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for diabetes, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of diabetes through interactive case studies and self-assessment tools.'
  },



  
  'galactosemia': {
    'about': "A carbohydrate metabolism disorder that affects galactose to glucose conversion.",
    'pathogenesis': `Galactosemia is an inherited disorder that typically follows an autosomal recessive inheritance pattern. The metabolic disorder is caused by severe impairment or deficiency of the enzymes involved in the Leloir pathway, which are responsible for galactose metabolism. The impairments or deficiencies of these enzymes result in the accumulation of toxic metabolites, primarily galactose-1-phosphate and galactitol, UDP‐hexoses alterations and subsequent impaired glycosylation, ER stress, signalling pathway alterations, and oxidative stress.: 
- Type 1, also known as classic galactosemia, is characterised by severe deficiency of the enzyme galactose-1-phosphate uridylyltransferase (GALT), caused by mutations in the GALT gene. 
- Type 2 is characterised by the deficiency of galactokinase (GALK). 
- Type 3 is caused by a deficiency of the GALE enzyme (UDP‐galactose 4′‐epimerase). 
- Type 4 has been recently described as being caused by biallelic pathogenic variants in the galactose mutarotase gene, reducing the activity of the GALM enzyme. 
Potential mechanisms include glycosylation abnormalities, UDP‐hexoses abnormalities, and inhibition of galactosyltransferases by high levels of galactose‐1‐phosphate.²⁴

Type 1 (Classic):
- Characterized by severe deficiency of the GALT enzyme
- Caused by mutations in the GALT gene
- Most common and severe form
Type 2:
- Characterized by deficiency of galactokinase (GALK)
- Affects initial galactose processing
- Variable severity
Type 3:
- Caused by deficiency of the GALE enzyme (UDP-galactose 4′-epimerase)
- Variable presentation
Type 4:
- Recently identified form
- Caused by biallelic pathogenic variants in galactose mutarotase gene
- Reduces GALM enzyme activity`,
    'symptoms': `In the first weeks of life, initial signs include refusal to feed, vomiting, lethargy, jaundice, cataracts, and sepsis.²² When left untreated, clinical presentations include liver damage, neural damage, aminoaciduria, and failure to thrive.²¹ The most severe symptoms occur in patients with type 1 galactosemia, while patients with the other subtypes can vary from mild to severe symptoms. Galactosemia can also lead to other complications, such as organ damage (typically eyes, brain, and liver), bacterial infections, and premature ovarian insufficiency.²²

    Early Manifestations (First Weeks):
- Refusal to feed
- Vomiting
- Lethargy
- Jaundice
- Cataracts
- Sepsis

Progressive Symptoms:
- Liver dysfunction
- Growth failure
- Developmental delays
- Neurological complications
- Vision problems
- Feeding difficulties`,
    'prognosis': `Early Treatment Impact:
Without treatment in the first 10 days of life, classic galactosemia (Type 1) is typically fatal due to generalized infection. Untreated infants who survive the first month often develop cirrhosis. However, with early diagnosis and proper treatment, outcomes can include normal mental development along with normal function of other affected organs. Even with adequate treatment, children may have a lower intelligence quotient (IQ) and often develop speech and balance problems during adolescence.

Long-term Outcomes:
Children who are diagnosed and treated early usually grow and develop adequately, even though they may still experience some difficulties due to endogenous galactose being produced by the body. Without appropriate dietary therapy, long-term complications such as speech deficits, neurological problems, and mental impairment occur in older children. Progressive organ damage is common in untreated cases, potentially leading to fatality in infancy or the development of cirrhosis.

Adult Outcomes:
Adults with galactosemia can live relatively normal lives with proper treatment, although those who experienced symptoms during childhood may continue to experience lifelong symptoms. Some symptoms may fluctuate depending on how well adults maintain their restricted diet, while others are common despite treatment. Ongoing monitoring remains necessary throughout adulthood to manage potential complications.`,
    'risk-factors': `Genetic and Population Factors:
Galactosemia follows an autosomal recessive inheritance pattern. When both parents carry mutated copies of the affected gene (most commonly GALT, less frequently GALK, GALE, or GALM), each child has a 25% chance of being affected.

Population Distribution:
- Affects males and females equally
- Higher prevalence in Caucasian populations
- Variable frequency across ethnic groups`,
    'diagnosis-and-treatment': `DIAGNOSIS:
Galactosemia is included in most newborn screening programs:
- Blood sample from heel stick
- Genetic testing on infant blood sample
- Amniotic fluid testing for fetal diagnosis
Diagnostic Criteria:
- Elevated galactose-1-phosphate in red blood cells
- Reduction in GALT enzyme activity
- Genetic testing for type determination
- Comprehensive metabolic analysis

TREATMENT:
While there is no cure for galactosemia, effective management strategies exist:
Dietary Management:
- Strict dairy-free diet required
- Life-long galactose restriction
- Elimination of dairy products
- Limited galactose from non-milk sources permitted
Nutritional Monitoring:
- Annual dietary assessments
- Calcium intake evaluation
- Vitamin D level monitoring
- Supplementation as needed
Follow-up Protocol:
Red blood cell galactose-1-phosphate measurements:
- Initial check at 3 months
- Follow-up at 9 months
- Yearly measurements thereafter
- Baseline establishment
- Regular monitoring of growth and development`,
    'learning-tools': `AI-generated Case Study:
A 10-day-old female infant was brought to the emergency department by her parents due to persistent vomiting, poor feeding, and increasing lethargy over the past 48 hours. Her parents also noticed yellowing of her skin (jaundice) and irritability. She had been breastfed since birth but showed difficulty gaining weight.
Her medical history included a full-term birth with no complications. On examination, the infant appeared dehydrated and lethargic. She had a weight of 3.2 kg, a temperature of 37.2°C, a heart rate of 140 beats per minute, and a respiratory rate of 42 breaths per minute. Physical examination revealed jaundice, hepatomegaly (enlarged liver), hypotonia (low muscle tone), and poor responsiveness.

AI-generated Flashcards:
Click here to access a custom AI-generated flashcard deck based on the content in this module:
<Link to="https://anki-decks.com/deck/detail/AEq4JtyCo/">Access Galactosemia Flashcards →</Link>`,
    'pathogenesis-outcome': 'Explain the pathogenic mechanisms and biochemical basis of galactosemia, including its molecular and cellular effects.',
    'prognosis-outcome': 'Evaluate the clinical progression and long-term outcomes associated with galactosemia, including factors that influence disease severity.',
    'risk-factors-outcome': 'Identify and analyze the key risk factors and genetic components contributing to the development of galactosemia.',
    'diagnosis-and-treatment-outcome': 'Understand the diagnostic criteria and current treatment approaches for galactosemia, including management strategies and therapeutic interventions.',
    'learning-tools-outcome': 'Apply knowledge of galactosemia through interactive case studies and self-assessment tools.'
  }
};

export default diseaseContent; 